import type { ValidationReport } from "@/packages/application";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useTranslation } from "@/packages/utils";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Search } from "lucide-react";
import { useMemo, useState } from "react";

interface AuditSubmissionBlockersModalProps {
  open: boolean;
  onClose: () => void;
  validationReport: ValidationReport;
}

type IssueCategory =
  | "completion"
  | "required_items"
  | "document_coverage"
  | "other";

const CATEGORY_ORDER: IssueCategory[] = [
  "completion",
  "required_items",
  "document_coverage",
  "other",
];

export function AuditSubmissionBlockersModal({
  open,
  onClose,
  validationReport,
}: AuditSubmissionBlockersModalProps) {
  const { t } = useTranslation(["audits", "common"]);
  const [expandedCategories, setExpandedCategories] = useState<
    Set<IssueCategory>
  >(new Set(CATEGORY_ORDER));
  const [search, setSearch] = useState("");

  const groupedIssues = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? validationReport.issues.filter((issue) =>
          issue.message.toLowerCase().includes(query),
        )
      : validationReport.issues;

    const groups: Map<IssueCategory, typeof filtered> = new Map();
    for (const cat of CATEGORY_ORDER) {
      groups.set(cat, []);
    }
    for (const issue of filtered) {
      const cat = issue.category;
      const existing = groups.get(cat) ?? [];
      existing.push(issue);
      groups.set(cat, existing);
    }

    return CATEGORY_ORDER.map((cat) => ({
      category: cat,
      issues: groups.get(cat) ?? [],
    })).filter((g) => g.issues.length > 0);
  }, [validationReport.issues, search]);

  const totalIssues = validationReport.issues.length;
  const categoryCount = groupedIssues.length;

  function toggleCategory(cat: IssueCategory) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  }

  return (
    <ConfirmDialog open={open} onClose={onClose}>
      <ConfirmDialog.Title>
        {t("audits:actions.submitBlockers.title")}
      </ConfirmDialog.Title>
      <ConfirmDialog.Body>
        <div className="space-y-4">
          <p className="text-sm text-secondary">
            {t("audits:actions.submitBlockers.body")}
          </p>

          {validationReport.issues.length > 0 ? (
            <div className="space-y-3">
              {/* Summary bar */}
              <div className="flex items-center gap-2 text-xs text-muted">
                <span>
                  {t("audits:actions.submitBlockers.summary", {
                    count: categoryCount,
                    total: totalIssues,
                  })}
                </span>
              </div>

              {/* Search input */}
              <div className="relative">
                <Search
                  size={13}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t(
                    "audits:actions.submitBlockers.searchPlaceholder",
                  )}
                  className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 pl-7 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-accent/50"
                />
              </div>

              {/* Grouped issues */}
              <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                {groupedIssues.length === 0 ? (
                  <p className="text-sm text-muted text-center py-4">
                    {t("audits:actions.submitBlockers.noResults")}
                  </p>
                ) : (
                  groupedIssues.map(({ category, issues }) => {
                    const isExpanded = expandedCategories.has(category);
                    return (
                      <div
                        key={category}
                        className="rounded-lg border border-border/60 overflow-hidden"
                      >
                        {/* Group header */}
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3 py-2.5 bg-surface/60 hover:bg-surface transition-colors text-left"
                          onClick={() => toggleCategory(category)}
                        >
                          <span className="text-sm font-medium text-primary">
                            {t(
                              `audits:actions.submitBlockers.categoryLabels.${category}`,
                            )}
                          </span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-muted tabular-nums">
                              {issues.length}
                            </span>
                            <motion.span
                              animate={{ rotate: isExpanded ? 90 : 0 }}
                              transition={{ duration: 0.15 }}
                              className="text-muted"
                            >
                              <ChevronRight size={13} />
                            </motion.span>
                          </div>
                        </button>

                        {/* Collapsible issue list */}
                        <AnimatePresence initial={false}>
                          {isExpanded && (
                            <motion.div
                              key={`${category}-issues`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <ul className="px-3 py-2 space-y-1.5 border-t border-border/40">
                                {issues.map((issue) => (
                                  <li
                                    key={`${issue.category}-${issue.message}`}
                                    className="text-sm text-secondary flex gap-2"
                                  >
                                    <span
                                      className={
                                        issue.severity === "error"
                                          ? "text-red-400 shrink-0"
                                          : "text-amber-400 shrink-0"
                                      }
                                    >
                                      •
                                    </span>
                                    <span>
                                      {issue.messageKey
                                        ? // Dynamic i18n key from use-case layer — keys are always valid audits namespace keys
                                          (
                                            t as (
                                              k: string,
                                              p?: Record<
                                                string,
                                                string | number
                                              >,
                                            ) => string
                                          )(
                                            issue.messageKey,
                                            issue.messageParams,
                                          )
                                        : issue.message}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-secondary">
              {t("audits:actions.submitBlockers.loadError")}
            </p>
          )}
        </div>
      </ConfirmDialog.Body>
      <ConfirmDialog.Actions
        onConfirm={onClose}
        confirmLabel={t("common:buttons.close")}
        cancelLabel=""
        variant="default"
      />
    </ConfirmDialog>
  );
}
